/**
 * List of in application messages
 *
 * This really could be plain json, but adding here as a JS with some helpers, and to mimic getting the JSON as if it were through a rest client.
 *
 * Detailed instructions on how to add a new message type are in the comments after the current list of message types.
 *
 */

import format from 'format-util';
import languageStrings from './fallbackData/languageStrings';

export const TYPES = {
  SYSTEM_ERROR: 'systemError', // General system error (backend error during processing), data inconsistency (unrecognised enum) etc
  NETWORK_ERROR: 'networkError', // Connectivity failure between front and backend
  APPLICATION_ERROR: 'applicationError', // Typically incorrect form entry
  APPLICATION_WARNING: 'applicationWarning', // Typically not a user error, but for example no search results
  APPLICATION_SUCCESS: 'applicationSuccess', // Success, the action has proceeded as expected
  APPLICATION_INFO: 'applicationInfo' // Info, informational message that can provide a user with the current state of application
};

export const SYS_ERROR_DICTIONARY_IDS = {
  // Base messages we MUST have at all costs.
  ID_NOT_FOUND: 0,
  CANNOT_LOAD_ERRORS: 'Sys_Error_2',
}

export const NETWORK_ERROR_DICTIONARY_IDS = {
  CONNECTION_ERROR: 'Network_Error_1',
}

export const APP_ERROR_DICTIONARY_IDS = {
  INVALID_USERNAME: 'App_Error_1',
  BLANK_PASSWORD: 'App_Error_2',
  INVALID_EMAIL: 'App_Error_3',
  BLANK_OLD_PASSWORD: 'App_Error_4',
  BLANK_NEW_PASSWORD: 'App_Error_5',
  BLANK_REENTER_PASSWORD: 'App_Error_6',
  NEW_REENTER_PASSWORD_MISMATCH: 'App_Error_7',
  BLANK_TAX_NUMBER: 'App_Error_8',
  BLANK_LOGIN: 'App_Error_9',
  BLANK_XML_SIGNATURE_KEY: 'App_Error_10',
  BLANK_XML_EXCHANGE_KEY: 'App_Error_11',
  INVALID_TAX_NUMBER: 'App_Error_12',
  BLANK_COMPANY_CODE: 'App_Error_13',
  BLANK_COMPANY_NAME: 'App_Error_14',
  BLANK_COMPANY_STREET: 'App_Error_15',
  BLANK_COMPANY_COUNTRY: 'App_Error_16',
  BLANK_COMPANY_POSTCODE: 'App_Error_17',
  BLANK_VATNUMBER_COUNTRY: 'App_Error_18',
  BLANK_VATNUMBER: 'App_Error_19',
  BLANK_VAT_NUMBER: 'App_Error_20',
  BLANK_EC_TYPE: 'App_Error_21',
  INVALID_AMOUNT: 'App_Error_22',
  BLANK_ACCOUNTID: 'App_Error_23',
  BLANK_LICENSEKEY: 'App_Error_24',
  CANNOT_LOAD_REPORT_META_DATA: 'App_Error_25',
  BLANK_PARENT_COMPANY: 'App_Error_26',
  BLANK_CHILD_COMPANY: 'App_Error_27',
  FAILED_REOPEN_REPORT: 'App_Error_28',
  REPORT_NOT_FOUND: 'App_Error_29',
  DUPLICATE_FILE: 'App_Error_30',
  LARGE_FILE: 'App_Error_31',
  TRANSACTION_REDELIVER_ERROR: 'App_Error_32',
  TRANSACTION_ACTION_ERROR: 'App_Error_33',
  INCORRECT_OLD_PASSWORD: 'App_Error_34',
  BLANK_REPORTINGCOMBINATION_VAT_CODE: 'App_Error_35',
  BLANK_REPORTINGCOMBINATION_COUNTRY: 'App_Error_36',
  BLANK_REPORTINGCOMBINATION_COMBINATION_ID: 'App_Error_37',
  BLANK_REPORTINGCOMBINATION_COMBINATION_ID_CREDIT: 'App_Error_38',
  BLANK_REPORTINGCOMBINATION_GOODTYPE: 'App_Error_39',
  BLANK_REPORTINGCOMBINATION_MAIN_VAT_CODE: 'App_Error_40',
  BLANK_REPORTINGCOMBINATION_IVAT_TRANSACTION_ID: 'App_Error_41',
  BLANK_REPORTINGCOMBINATION_USER_CODE: 'App_Error_42',
  VATCODE_FAILED_TO_CREATE: 'App_Error_43',
  VATCODE_ALREADY_EXISTS: 'App_Error_44',
  MTD_AUTH_FAILED: 'App_Error_45',
  VAT_RETURN_SUBMISSION_FAILED: 'App_Error_46',
  BLANK_AUTH_CODE: 'App_Error_47',
  FAILED_TO_IMPORT_VATCODES: 'App_Error_48',
  FAILED_TO_IMPORT_GENERIC_VATCODES: 'App_Error_49',
  BLANK_CURRENCY: 'App_Error_50',
  BLANK_TRANSACTION_DATE: 'App_Error_51',
  BLANK_INVOICE_DATE: 'App_Error_52',
  BLANK_BOX_NAME: 'App_Error_53',
  BLANK_BOX_VALUE: 'App_Error_54',
  CREATE_MANUAL_DOCUMENT_NO_FILE_DATA: 'App_Error_55',
  MTD_VAT_NUMBET_NOTAUTH: 'App_Error_56',
  VAT_BOX_REQUIRED: 'App_Error_57',
  FAILED_TO_RETRIEVE_EXCHANGE_RATES: 'App_Error_58',
  VAT_CODES_REDELIVER_ERROR_WITH_VAT_CODES: 'App_Error_59',
  VAT_CODES_REDELIVER_ERROR: 'App_Error_60',
  INVALID_MASQUERADE_CREDENTIALS: 'App_Error_61',
  PROCESSED_FILE_REDELIVER_ERROR: 'App_Error_62',
  INVALID_PASSWORD: 'App_Error_63',
  BLANK_PASSWORD_WITH_PARAM: 'App_Error_64',
  WEAK_NEW_PASSWORD: 'App_Error_65',
  MIN_LENGTH_PASSWORD: 'App_Error_66',
  PROCESSED_MULTIPLE_FILES_REDELIVER_ERROR: 'App_Error_67',
  RESERVED_PASSWORD: 'App_Error_68',
  CLOSED_RETURNS_NOREPORT_ERROR: 'App_Error_69',
  VIRUS_SCAN_FAILED: 'App_Error_70',
  UNABLE_TO_PROCESS_FILE: 'App_Error_71',
  INVALID_DATE_RANGE: 'App_Error_72',
  DUPLICATE_REPORTINGCOMBINATION_VAT_CODE: 'App_Error_73',
  FILE_DELETED_ERROR: 'App_Error_74',
  FILE_DELETED_ERROR_STILL_PROCESSING: 'App_Error_135',
  FILE_DELETED_ERROR_FAILED_PROCESSING: 'App_Error_136',
  FILE_DELETED_ERROR_NOT_FOUND: 'App_Error_137',
  FILE_DELETED_ERROR_ID_LIST: 'App_Error_138',
  FILE_DELETED_UNHANDLED_ERROR: 'App_Error_139',
  FILE_IMPORT_ERROR: 'App_Error_75',
  USER_UPDATE_ERROR: 'App_Error_76',
  USER_UPDATE_UNAUTHORIZED_ERROR: 'App_Error_77',
  BLANK_VATCODESEARCH_VAT_CODE: 'App_Error_78',
  BLANK_VATCODESEARCH_COMPANY_ID: 'App_Error_79',
  BLANK_VATCODESEARCH_MAIN_COUNTRY: 'App_Error_80',
  BLANK_VATCODESEARCH_START_DATE: 'App_Error_81',
  BLANK_VATCODESEARCH_MAIN_VAT_CODE: 'App_Error_82',
  BLANK_VATCODESEARCH_DEDUCTIBLE_VAT: 'App_Error_83',
  VATCODE_UPDATE_400: 'App_Error_84',
  VATCODE_UPDATE_UNKNOWN_ERROR: 'App_Error_85',
  VAT_CODE_DELETE_FAILURE: 'App_Error_86',
  VATCODE_CREATE_400: 'App_Error_87',
  VATCODE_CREATE_UNKNOWN_ERROR: 'App_Error_88',
  LOCALLISTING_INVALID_AMOUNT: 'App_Error_89',
  LOCALLISTING_INVALID_AMOUNTECLVAT: 'App_Error_90',
  LOCALLISTING_INVALID_AMOUNTVAT: 'App_Error_91',
  MAX_LENGTH_PASSWORD: 'App_Error_92',
  SAME_AS_USERNAME_PASSWORD: 'App_Error_93',
  CUSTOM_DATES_NO_VATNUMBER: 'App_Error_94',
  FILE_IMPORT_ERROR_EXTENSION_FORBIDDEN: 'App_Error_95',
  FILE_IMPORT_ERROR_EXTENSION_TOO_LONG: 'App_Error_96',
  FILE_IMPORT_ERROR_FILENAME_TOO_LONG: 'App_Error_97',
  FILE_IMPORT_ERROR_FILENAME_FORBIDDEN: 'App_Error_98',
  FILE_IMPORT_ERROR_CONTENTTYPE_FORBIDDEN: 'App_Error_99',
  FILE_IMPORT_ERROR_CONTENTTYPE_MISMATCH: 'App_Error_100',
  SUBMIT_FILING_DE_BLANK_PIN: 'App_Error_101',
  SUBMIT_FILING_DE_BLANK_FILE: 'App_Error_102',
  SUBMIT_FILING_DE_MAX_PIN: 'App_Error_103',
  SUBMIT_FILING_NL_BLANK_USERNAME: 'App_Error_104',
  SUBMIT_FILING_NL_BLANK_PASSWORD: 'App_Error_105',
  SUBMIT_FILING_NL_CURRENT_ERROR_STATES: 'App_Error_106',
  SUBMIT_FILING_NL_ERROR: 'App_Error_107',
  INVALID_SII_CERTIFICATE: 'App_Error_108',
  INVALID_SII_CERTIFICATE_PASSWORD: 'App_Error_109',
  INVALID_SII_CERTIFICATE_EXPIRED: 'App_Error_110',
  BLANK_SII_CERTIFICATE_PASSWORD: 'App_Error_111',
  SII_CERTIFICATE_ERROR: 'App_Error_112',
  SAVE_SII_CERTIFICATE_ERROR: 'App_Error_113',
  SII_STARTDATE_VALIDATION_ERROR: 'App_Error_114',
  SII_ENDDATE_VALIDATION_ERROR: 'App_Error_115',
  INVALID_SII_CERTIFICATE_PASSWORD_FORMAT: 'App_Error_116',
  SII_CERTIFICATE_NOT_PARSED: 'App_Error_117',
  NO_SII_CERTIFICATE_PROVIDED: 'App_Error_118',
  BLANK_SII_CERTIFICATE: 'App_Error_119',
  MERGE_XML_FILE_ERROR: 'App_Error_120',
  MERGE_XML_FILE_VALIDATION: 'App_Error_121',
  VAT_BOOK_STYLESHEET_LOAD_ERROR: 'App_Error_122',
  COUNTRIES_UNLOCKED_ERROR: 'App_Error_123',
  COUNTRIES_LOCKED_ERROR: 'App_Error_124',
  ALL_COUNTRIES_UNLOCKED_ERROR: 'App_Error_125',
  ALL_COUNTRIES_LOCKED_ERROR: 'App_Error_126',
  BLANK_OSSUNION_REGNR: 'App_Error_127',
  BLANK_OSSNONUNION_REGNR: 'App_Error_128',
  BLANK_iOSS_REGNR: 'App_Error_129',
  BLANK_OSSUNION_COUNTRY: 'App_Error_130',
  BLANK_OSSNONUNION_COUNTRY: 'App_Error_131',
  BLANK_iOSS_COUNTRY: 'App_Error_132',
  BLANK_iOSS_INTR_REGNR_COUNTRY: 'App_Error_133',
  ECL_VATNUMBER_CHECK_ERROR: 'App_Error_134',
  RESET_PASSWORD_ERROR: 'App_Error_140',
  FAILED_TO_IMPORT_EXCHANGE_RATES: 'App_Error_141',
  FAILED_TO_IMPORT_EXCHANGE_RATES_LARGE_FILE_SIZE: 'App_Error_142',
  INVALID_FIRST_NAME: 'App_Error_143',
  INVALID_LAST_NAME: 'App_Error_144',
  FAILED_TO_SAVE_TO_WORKFLOW: 'App_Error_145',
  ERRORS_WARNINGS_REOPEN_FAILURE: 'App_Error_146',
  ERRORS_WARNINGS_REOPEN_PARTIAL_FAILURE: 'App_Error_147',
  USER_CREATE_ERROR: 'App_Error_148',
  USER_DELETE_ERROR: 'App_Error_149',
  USER_DELETE_VALIDATION: 'App_Error_150',
  INVALID_CORRECTION_PERIOD: 'App_Error_151',
  FAILED_RESCHEDULE_CORRECTION: 'App_Error_152',
  INVALID_NON_CORRECTION_PERIOD: 'App_Error_153',
  FAILED_RESCHEDULE_NON_CORRECTION: 'App_Error_154',
  ACCOUNT_SETTINGS_FORM_ERROR: 'App_Error_155',
  SAVE_TO_WORKFLOW_FAIL: 'App_Error_156',
  FAILED_CREDIT_NOTE_CERTIFICATE_SUBMISSION: "App_Error_157",
  VAT_BOOK_ALREADY_CLOSED: "App_Error_158",
  FAILED_CLOSE_VAT_BOOK: "App_Error_159",
  BLANK_COMPANY: "App_Error_160",
  NO_INPUT_TYPE: "App_Error_161",
  BLANK_REASON: "App_Error_162",
  BLANK_TRANSACTION_YEAR: "App_Error_163",
  NO_FILE: "App_Error_164",
  BULK_ACTION_INVALID_REQUEST: "App_Error_165",
  BULK_ACTION_GENERIC_ERROR: "App_Error_166",
  BULK_ACTION_FAILED: "App_Error_167",
  BULK_ACTION_INVALID_ENTITIES_RETURNED: "App_Error_168",
  BULK_ACTION_API_ERROR: "App_Error_169",
  FILE_OPERATION_ERROR: "App_Error_170",
  GET_OPERATION_STATUS_ERROR: "App_Error_171",
  COMPANY_PERMISSION_UPDATE_ERROR: "App_Error_172",
  COMPANY_PERMISSION_LOAD_ERROR: "App_Error_173",
  INTRASTAT_RETURNS_GENERATION_ERROR: 'App_Error_174',
  AUTO_SUBMISSION_INITIATION_FAILED: 'App_Error_175',
  IMPORT_EXCHANGE_RATES_GENERIC_ERROR: 'App_Error_176',
  VAT_RETURN_GENERATION_ERROR: 'App_Error_177',
  FAILED_CLOSE_VAT_RETURN: 'App_Error_178',
  FAILED_CLOSE_INTRASTAT: 'App_Error_179',
  MANUAL_DOCS_REQUIRES_COMPANY_ERROR: 'App_Error_180',
  API_LICENSE_KEY_RETRIEVE_ERROR: 'App_Error_181',
  UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR: 'App_Error_183',
  VAT_RETURN_SUBMISSION_AUTH_FAILED: 'App_Error_184',
  RETURN_APPROVAL_SEND_FAILED: 'App_Error_185'
};

export const WARNING_DICTIONARY_IDS = {
  INVALID_LOGIN_CREDENTIALS: 'Warning_1',
  DUPLICATE_USER_ID: 'Warning_2',
  REPORT_ALREADY_APPROVED: 'Warning_3',
  DUPLICATE_COMPANY_CODE: 'Warning_4',
  COMPANIES_IMPORT_FAILED: 'Warning_5',
  VAT_CODE_UNSUPPORTED_9999: 'Warning_6',
  VAT_CODE_UNSUPPORTED_9000: 'Warning_7',
  ERRORS_WARNINGS_ACTION_RECORDS_SKIPPED: 'Warning_8',
  SUBMIT_FILING_NL_CURRENT_PENDING_STATES: 'Warning_9',
  SUBMIT_FILING_NL_PENDING: 'Warning_10',
  VAT_PL_FILING_PREVIEW: 'Warning_11',
  EXCHANGE_RATES_IMPORT_WARNING: 'Warning_12',
  REPORT_PAGE_SIZE_LOAD: 'Warning_13',
  VAT_RETURN_AUTO_FETCH_FAILED: 'Warning_14',
  INVALID_DEEP_LINK_INFO: 'Warning_15',
  MTD_SUBMISSION_WARNING: 'Warning_16',
  MTD_SUBMISSION_WARNING_WITH_ERROR: 'Warning_17',
  RETURN_APPROVAL_VOIDED: 'Warning_18',
  RETURN_APPROVAL_APPROVED_OUTDATED: 'Warning_19',
  RETURN_APPROVAL_REJECTED_OUTDATED: 'Warning_20',
  RETURN_APPROVAL_EXPIRED: 'Warning_21',
  RETURN_APPROVAL_UNAVAILABLE: 'Warning_22'
};

export const SUCCESS_DICTIONARY_IDS = {
  CREATE_NEW_VAT_GROUP_SUCCESS: 'Success_1',
  RESET_PASSWORD_EMAIL_SENT_SUCCESS: 'Success_2',
  NEW_PASSWORD_SET_SUCCESS: 'Success_3',
  VAT_RETURN_SUBMIT_SUCCESS: 'Success_4',
  MTD_AUTH_SUCCESS: 'Success_5',
  CREATE_MANUAL_DOCUMENT_SUCCESS: 'Success_6',
  TRANSACTION_ACTION_SUCCESS: 'Success_7',
  EXCHANGE_RATES_IMPORT_SUCCESS: 'Success_8',
  VAT_CODES_REDELIVER_SUCCESS: 'Success_10',
  PROCESSED_FILE_REDELIVER_SENT_SUCCESS: 'Success_11',
  PROCESSED_FILE_REDELIVER_SUCCESS: 'Success_12',
  PROCESSED_MULTIPLE_FILES_REDELIVER_SUCCESS: 'Success_13',
  FILE_DELETED_SUCCESSFULLY: 'Success_14',
  VAT_CODE_CREATED_SUCCESSFULLY: 'Success_15',
  FILE_IMPORT_SUCCESS: 'Success_16',
  COMPANIES_IMPORTED_SUCCESSFULLY: 'Success_17',
  USER_UPDATE_SUCCESS: 'Success_18',
  VAT_CODE_UPDATED_SUCCESSFULLY: 'Success_19',
  VAT_CODE_DELETED_SUCCESSFULLY: 'Success_20',
  INVITE_EMAIL_SENT: 'Success_21',
  SUBMIT_FILING_DE_FILE_UPLOAD_SUCCESS: 'Success_22',
  SUBMIT_FILING_NL_SUCCESS: 'Success_23',
  SUBMIT_FILING_NL_ALREADY_SUCCESS: 'Success_24',
  VALID_SII_CERTIFICATE: 'Success_25',
  COUNTRIES_UNLOCKED_SUCCESS: 'Success_26',
  COUNTRIES_LOCKED_SUCCESS: 'Success_27',
  ALL_COUNTRIES_UNLOCKED_SUCCESS: 'Success_28',
  ALL_COUNTRIES_LOCKED_SUCCESS: 'Success_29',
  SAVED_TO_WORKFLOW: 'Success_30',
  ERRORS_WARNINGS_REOPENED: 'Success_31',
  USER_DELETE_SUCCESS: 'Success_32',
  SAVE_TO_WORKFLOW_OK: 'Success_33',
  BULK_ACTION_COMPLETED: 'Success_34',
  FILE_OPERATION_STARTED_SUCCESS: 'Success_35',
  COMPANY_PERMISSION_UPDATE_SUCCESS: 'Success_36',
  INITIATED_AUTO_SUBMISSION: 'Success_37',
  IMPORT_EXCHANGE_RATES_SUCCESS: 'Success_38',
  DOCUMENT_DELETE_OPERATION_STARTED_MESSAGE: 'Success_40',
  DOCUMENT_REDELIVER_OPERATION_STARTED_MESSAGE: 'Success_41',
  RETURN_APPROVAL_SENT: 'Success_42',
};

export const INFO_DICTIONARY_IDS = {
  DOCUMENTS_ON_HOLD: 'Info_1',
  TRANSACTIONS_PENDING: 'Info_2',
  ALL_TRANSACTIONS_PROCESSED: 'Info_3',
  DOCUMENTS_IN_PROCESSING: 'Info_4',
  // Returns generation
  VAT_BOOKS_RETURNS_GENERATION_PENDING: 'Info_5',
  BULK_ACTION_INITIATED: 'Info_6',
  BULK_ACTION_IN_PROGRESS: 'Info_7',
  BULK_OPERATION_STATUS_NODATA: 'Info_8',
  // Intrastat generation
  INTRASTAT_RETURNS_GENERATION_PENDING: 'Info_9',
  // Exchange Rates
  IMPORT_EXCHANGE_RATES_STATUS_NODATA: 'Info_10',
  IMPORT_EXCHANGE_RATES_STARTED: 'Info_11',
  // VAT Return generation
  VAT_RETURN_GENERATION_PENDING: 'Info_13',
  // Uploaded Documents
  UPLOADED_DOCUMENTS_COMPANY_FILTER_PROCESSED_ONLY: 'Info_14'
}

export const ALL_DICTIONARY_IDS = Object.assign({}, SYS_ERROR_DICTIONARY_IDS, NETWORK_ERROR_DICTIONARY_IDS, APP_ERROR_DICTIONARY_IDS, WARNING_DICTIONARY_IDS, SUCCESS_DICTIONARY_IDS, INFO_DICTIONARY_IDS);

/**
 * This is the list of in-app messages which must always be available to the app, even if everything else fails.
 */
const BASE_MESSAGE_LIST = [
  // Start of System errors
  {
    id: SYS_ERROR_DICTIONARY_IDS.ID_NOT_FOUND, type: TYPES.SYSTEM_ERROR,
    condition: 'Error occurs if an in-app error ID is used which is not recognised',
  },
  {
    id: SYS_ERROR_DICTIONARY_IDS.CANNOT_LOAD_ERRORS, type: TYPES.SYSTEM_ERROR,
    condition: 'Error occurs if loading & validating the list of errors from the backend could not be successfully performed'
  },
  // End of system errors

  // Start of Network errors
  {
    id: NETWORK_ERROR_DICTIONARY_IDS.CONNECTION_ERROR, type: TYPES.NETWORK_ERROR,
    condition: 'Error occurs if the app is unable to connect to the backend'
  },
  // End of Network errors

  // Start of Application errors
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_USERNAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user types a username which is not an email address'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user leaves their password blank in the login page'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user enters a invalid password'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MIN_LENGTH_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the password length is less than the minimum required length'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_EMAIL, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user types a email which is not an valid email address'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_OLD_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user leaves their old password blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_NEW_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user leaves their new password blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REENTER_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user leaves their reenter password blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.NEW_REENTER_PASSWORD_MISMATCH, type: TYPES.APPLICATION_ERROR,
    condition: 'When you select a new password, you must always type the password twice to confirm it. You did this, and the two passwords you typed do not match.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_TAX_NUMBER, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a tax number value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_TAX_NUMBER, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user enters a tax number with more than 8 characters'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_LOGIN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a login value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_XML_EXCHANGE_KEY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a xml exchange key value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_XML_SIGNATURE_KEY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a xml signature key value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VAT_NUMBER, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a tax number value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_EC_TYPE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a EC Type value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a company code value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY_NAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a company name value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY_STREET, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a company street value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a company country value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY_POSTCODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a company postcode value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATNUMBER_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a VAT number country value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATNUMBER, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a VAT number value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_ACCOUNTID, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a account id value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_LICENSEKEY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter a license key value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_AMOUNT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter correct amount value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.CANNOT_LOAD_REPORT_META_DATA, type: TYPES.APPLICATION_ERROR,
    condition: 'Error while fetching report meta data from server'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_PARENT_COMPANY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not select parent company'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_CHILD_COMPANY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not select child company'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_REOPEN_REPORT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the file with the given name already exists'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.REPORT_NOT_FOUND, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the file size is larger than max file size'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.TRANSACTION_REDELIVER_ERROR,
    type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the transaction redeliver is unsuccessful'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.TRANSACTION_ACTION_ERROR,
    type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the given action is failed for multiple transaction'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.DUPLICATE_FILE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the file with the given name already exists'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.LARGE_FILE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the file size is larger than max file size'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INCORRECT_OLD_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user enters incorrect old password'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_FAILED_TO_CREATE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the api could not save the submitted data'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_ALREADY_EXISTS, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the VAT Code was not saved because it already exists for that reporter'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_IMPORT_VATCODES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a VAT Codes from a file could not be imported'
  },

  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_VAT_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a Country'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_COMBINATION_ID, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a Combination ID'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_COMBINATION_ID_CREDIT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a Combination ID Credit'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_GOODTYPE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a Good Type'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_MAIN_VAT_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a Main VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_IVAT_TRANSACTION_ID, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a iVAT Transaction ID'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REPORTINGCOMBINATION_USER_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reporting combination does not have a User Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_RETURN_SUBMISSION_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if VAT return submission failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_RETURN_SUBMISSION_AUTH_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if VAT return submission failed due to missing or expired HMRC authentication'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.AUTO_SUBMISSION_INITIATION_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if auto submission initiation failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.RETURN_APPROVAL_SEND_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if sending a VAT return for approval failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MTD_AUTH_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if MTD Authentication failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_AUTH_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if Authorization Code is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.CREATE_MANUAL_DOCUMENT_NO_FILE_DATA, type: TYPES.APPLICATION_ERROR,
    condition: 'No file data was found'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_CURRENCY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user doest not enter currency value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_TRANSACTION_DATE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user doest not enter transaction date value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_INVOICE_DATE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user doest not enter invoice date value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_BOX_NAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user doest not enter box name value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_BOX_VALUE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user doest not enter box value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_IMPORT_GENERIC_VATCODES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if there was a failure importing the generic VAT codes'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_RETRIEVE_EXCHANGE_RATES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if there was a failure retrieving exchange rates'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MTD_VAT_NUMBET_NOTAUTH, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if VAT number is not authenticated with MTD'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_BOX_REQUIRED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user does not add at-least one vat box entry'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_CODES_REDELIVER_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if there was a failure redelivering VAT codes'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_CODES_REDELIVER_ERROR_WITH_VAT_CODES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if there was a failure redelivering VAT codes'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_MASQUERADE_CREDENTIALS, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the username, password or license key do not successfully authenticate with the backend service'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_PASSWORD_WITH_PARAM, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user leaves their password blank in the login page'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.WEAK_NEW_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the given password is not strong enough to consider as a valid password'
  }, {
    id: APP_ERROR_DICTIONARY_IDS.PROCESSED_FILE_REDELIVER_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if unable to redeliver the file'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.PROCESSED_MULTIPLE_FILES_REDELIVER_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if unable to redeliver multiple files'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.RESERVED_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if password contains the reserved word'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.CLOSED_RETURNS_NOREPORT_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if no report found'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VIRUS_SCAN_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if virus scan failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.UNABLE_TO_PROCESS_FILE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if unable to process file'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_DATE_RANGE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user select an invalid date range'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.DUPLICATE_REPORTINGCOMBINATION_VAT_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if VAT code already exists and has same start date'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when trying to delete a processed document fails due to being in a closed period'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_ERROR_STILL_PROCESSING, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when trying to delete a processed document fails due to still being processed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_ERROR_FAILED_PROCESSING, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when trying to delete a processed document fails due to having failed processing'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_ERROR_NOT_FOUND, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when trying to delete a processed document fails due to the document not being found'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_ERROR_ID_LIST, type: TYPES.APPLICATION_ERROR,
    condition: 'Error message contains a list of document IDs for which the specified action failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_DELETED_UNHANDLED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error message occurs when an unknown error is caught when deleting a file'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when trying to import XML account settings fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_EXTENSION_FORBIDDEN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an invalid extension'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_EXTENSION_TOO_LONG, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an overly long extension'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_FILENAME_TOO_LONG, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an overly long filename'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_FILENAME_FORBIDDEN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an invalid filename'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_CONTENTTYPE_FORBIDDEN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an invalid content type'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_IMPORT_ERROR_CONTENTTYPE_MISMATCH, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when a file is uploaded with an extension that does not match its content type'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.USER_UPDATE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user update fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.USER_UPDATE_UNAUTHORIZED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when user with Preparer role, updates user role. Only Admin user can update role.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_VAT_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no VAT Code is supplied.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_COMPANY_ID, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no Company is selected.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_MAIN_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no Main Country is selected.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_START_DATE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no Start Date is selected.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_MAIN_VAT_CODE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no Main VAT Code option is selected.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_VATCODESEARCH_DEDUCTIBLE_VAT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs on create VAT Code by search details were no deductible vat is supplied.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_UPDATE_400, type: TYPES.APPLICATION_ERROR,
    condition: 'When the API returns a 400 Bad Request attempting to update a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_UPDATE_UNKNOWN_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'When the API returns an unhandled failure when attempting to update a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_CODE_DELETE_FAILURE, type: TYPES.APPLICATION_ERROR,
    condition: 'When the API returns a failure when attempting to delete a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_CREATE_400, type: TYPES.APPLICATION_ERROR,
    condition: 'When the API returns a failure when attempting to create a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VATCODE_CREATE_UNKNOWN_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'When the API returns a failure when attempting to create a VAT Code'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.LOCALLISTING_INVALID_AMOUNT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter correct amount value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.LOCALLISTING_INVALID_AMOUNTECLVAT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter correct amount ecl vat value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.LOCALLISTING_INVALID_AMOUNTVAT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user does not enter correct amount vat value'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MAX_LENGTH_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user enter password with more than 100 characters'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SAME_AS_USERNAME_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user enter password same as user name'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.CUSTOM_DATES_NO_VATNUMBER, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the VAT Number is not setup for selected company & country'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_DE_BLANK_FILE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the certificate file not provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_DE_BLANK_PIN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the certificate PIN not provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_DE_MAX_PIN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user enter certificate PIN with more than 50 characters'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_NL_BLANK_USERNAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the username is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_NL_BLANK_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the password is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_NL_CURRENT_ERROR_STATES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs in filing NL report to digipoort'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SUBMIT_FILING_NL_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'In first time submission error occurs in filing NL report to digipoort'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MERGE_XML_FILE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when XML File merge Process Failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MERGE_XML_FILE_VALIDATION, type: TYPES.APPLICATION_ERROR,
    condition: 'Validation error occures on merge button click'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_SII_CERTIFICATE, type: TYPES.APPLICATION_ERROR,
    condition: 'Occurs if a user attempts to upload an invalid certificate when setting up SII'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_SII_CERTIFICATE_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Occurs if a user attempts to upload a certificate with an invalid password when setting up SII'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_SII_CERTIFICATE_EXPIRED, type: TYPES.APPLICATION_ERROR,
    condition: 'Occurs if a user attempts to upload a certificate that has expired when setting up SII'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_SII_CERTIFICATE_PASSWORD, type: TYPES.APPLICATION_ERROR,
    condition: 'Occurs if a user attempts to upload a certificate without entering a password'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SII_CERTIFICATE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'unkonw error occurs if a user attempts to upload a certificate'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SAVE_SII_CERTIFICATE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'unkonw error occurs if a user attempts to save a certificate to database'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SII_STARTDATE_VALIDATION_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error occures during regPrevioGGEEoREDEMEoCompetencia SII start date validation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SII_ENDDATE_VALIDATION_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error occures during regPrevioGGEEoREDEMEoCompetencia SII end date validation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_SII_CERTIFICATE_PASSWORD_FORMAT, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error occures if password format is incoorect while SII certificate validation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SII_CERTIFICATE_NOT_PARSED, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error occures if SII certificate is not parsed during certificate verification'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.NO_SII_CERTIFICATE_PROVIDED, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error occures if no SII certificate provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_SII_CERTIFICATE, type: TYPES.APPLICATION_ERROR,
    condition: 'validation error on VAT NUmber Save operation if password provided and no SII certificate provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_BOOK_STYLESHEET_LOAD_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when vat book stylesheet data load Failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.COUNTRIES_UNLOCKED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when countries on hold unlock failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.COUNTRIES_LOCKED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when countries on hold lock failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ALL_COUNTRIES_UNLOCKED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when all countries on hold unlock failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ALL_COUNTRIES_LOCKED_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occures when all countries on hold lock failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_OSSUNION_REGNR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS union registration number is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_OSSNONUNION_REGNR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS non-union registration number is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_iOSS_REGNR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS import registration number is blank'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_OSSUNION_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS union opted for country is blank and registration number provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_OSSNONUNION_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS non-union opted for country is blank and registration number provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_iOSS_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS import opted for country is blank and registration number provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_iOSS_INTR_REGNR_COUNTRY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if OSS import opted for country is blank and intermediary registration number provided'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ECL_VATNUMBER_CHECK_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if VAT number check failed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.RESET_PASSWORD_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurred in reset password operation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_IMPORT_EXCHANGE_RATES, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if there was a failure in importing exchange rates file'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_IMPORT_EXCHANGE_RATES_LARGE_FILE_SIZE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if exchange rate file size is too large'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_FIRST_NAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user types an invalid first name'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_LAST_NAME, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if the user types an invalid last name'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_TO_SAVE_TO_WORKFLOW, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if save to workflow fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ERRORS_WARNINGS_REOPEN_FAILURE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if returns fail to reopen.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ERRORS_WARNINGS_REOPEN_PARTIAL_FAILURE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when some, but not all, returns fail to reopen.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.USER_CREATE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user creation fails.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.USER_DELETE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user deletion fails.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.USER_DELETE_VALIDATION, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user try to delete itself.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_CORRECTION_PERIOD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user dont select future period for correction.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_RESCHEDULE_CORRECTION, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reschedule as a correction failed.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INVALID_NON_CORRECTION_PERIOD, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if user dont select future period for non-correction.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_RESCHEDULE_NON_CORRECTION, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if reschedule as a non-correction failed.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.ACCOUNT_SETTINGS_FORM_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if account settings form is invalid.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.SAVE_TO_WORKFLOW_FAIL, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if save to workflow fails.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_CREDIT_NOTE_CERTIFICATE_SUBMISSION, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if submit credit note certificate fails.'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_BOOK_ALREADY_CLOSED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to close a VAT Book that is already closed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_CLOSE_VAT_BOOK, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if closing a VAT Book fails for any reason other than it is already closed'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_COMPANY, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to fetch a list of transactions without selecting a company'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.NO_INPUT_TYPE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to fetch a list of transactions without selecting an input type'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_REASON, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to fetch a list of transactions without providing a reason'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BLANK_TRANSACTION_YEAR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to fetch a list of transactions without selecting a transaction year'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.NO_FILE, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a user attempts to fetch a list of transactions without providing an input file'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BULK_ACTION_INVALID_REQUEST, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if an invalid request has been sent for bulk operation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BULK_ACTION_GENERIC_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if any generic issue happens with the bulk operation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BULK_ACTION_FAILED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if all transactions fails in bulk operation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BULK_ACTION_INVALID_ENTITIES_RETURNED, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if an invalid request returns failing entities'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.BULK_ACTION_API_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if an invalid request occurs while validation in the API'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FILE_OPERATION_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a file operation fails in bulk operation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.GET_OPERATION_STATUS_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if a get operation status fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.COMPANY_PERMISSION_UPDATE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when updating company permissions for user'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.COMPANY_PERMISSION_LOAD_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when fetching users and company permissions'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.INTRASTAT_RETURNS_GENERATION_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs while intrastat return generation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.IMPORT_EXCHANGE_RATES_GENERIC_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if any generic issue happens with the import exchange rates'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.VAT_RETURN_GENERATION_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs while VAT return generation'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_CLOSE_VAT_RETURN, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if closing a VAT Return via the V6 CloseReturn endpoint fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.FAILED_CLOSE_INTRASTAT, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs if closing an Intrastat return via the V6 CloseReturn endpoint fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.MANUAL_DOCS_REQUIRES_COMPANY_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Shown when user searches for manual documents only without selecting a company'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.API_LICENSE_KEY_RETRIEVE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Error occurs when retrieving the API license key fails'
  },
  {
    id: APP_ERROR_DICTIONARY_IDS.UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR, type: TYPES.APPLICATION_ERROR,
    condition: 'Shown on the Uploaded Documents page when the filter start date is later than the end date'
  },

  // End of Application errors

  // Start of Warnings
  {
    id: WARNING_DICTIONARY_IDS.INVALID_LOGIN_CREDENTIALS, type: TYPES.APPLICATION_WARNING,
    condition: 'Error occurs if the username and password do not successfully authenticate with the backend service'
  },
  {
    id: WARNING_DICTIONARY_IDS.DUPLICATE_USER_ID, type: TYPES.APPLICATION_WARNING,
    condition: 'Error occurs if invited user already exists'
  },
  {
    id: WARNING_DICTIONARY_IDS.REPORT_ALREADY_APPROVED, type: TYPES.APPLICATION_WARNING,
    condition: 'This report is already approved'
  },
  {
    id: WARNING_DICTIONARY_IDS.DUPLICATE_COMPANY_CODE, type: TYPES.APPLICATION_WARNING,
    condition: 'Error occurs if the company with the given code already exists'
  },
  {
    id: WARNING_DICTIONARY_IDS.COMPANIES_IMPORT_FAILED, type: TYPES.APPLICATION_WARNING,
    condition: 'Error occurs when trying to import a companies document if file content is not valid'
  },
  {
    id: WARNING_DICTIONARY_IDS.VAT_CODE_UNSUPPORTED_9999, type: TYPES.APPLICATION_WARNING,
    condition: 'Occurs when a Transaction Id does not exist for this country'
  },
  {
    id: WARNING_DICTIONARY_IDS.VAT_CODE_UNSUPPORTED_9000, type: TYPES.APPLICATION_WARNING,
    condition: 'Occurs when a Transaction Id is not yet fully checked'
  },
  {
    id: WARNING_DICTIONARY_IDS.ERRORS_WARNINGS_ACTION_RECORDS_SKIPPED, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning occurs when trying to perform action on errors or warnings more than the maximum batch count'
  },
  {
    id: WARNING_DICTIONARY_IDS.SUBMIT_FILING_NL_CURRENT_PENDING_STATES, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning occurs when trying to re-submit NL filing to digipoort for report which is already in pending state'
  },
  {
    id: WARNING_DICTIONARY_IDS.SUBMIT_FILING_NL_PENDING, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning occurs when trying to submit NL filing to digipoort and its status is in pending state'
  },
  {
    id: WARNING_DICTIONARY_IDS.VAT_PL_FILING_PREVIEW, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning occurs when trying to preview VAT return for PL'
  },
  {
    id: WARNING_DICTIONARY_IDS.EXCHANGE_RATES_IMPORT_WARNING, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning occurs when trying to import exchange rates'
  },
  {
    id: WARNING_DICTIONARY_IDS.REPORT_PAGE_SIZE_LOAD, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning shows on invoice lines and SII transaction reports'
  },
  {
    id: WARNING_DICTIONARY_IDS.VAT_RETURN_AUTO_FETCH_FAILED, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning shows when async VAT Return auto-fetch polling stops after repeated failures'
  },
  {
    id: WARNING_DICTIONARY_IDS.INVALID_DEEP_LINK_INFO, type: TYPES.APPLICATION_WARNING,
    condition: 'Warning shows on the Companies page when a deep-linked companyId/vatNumber (e.g. from an MTD submission auth failure) could not be auto-opened - invalid companyId, company not found, or VAT number not found on that company'
  },
  {
    id: WARNING_DICTIONARY_IDS.MTD_SUBMISSION_WARNING, type: TYPES.APPLICATION_WARNING,
    condition: 'Advisory warning returned by HMRC on a SUCCESSFUL GB MTD VAT Return submission, e.g. authorization expiring. Rendered page-level by FilingPreparation, because MTDSubmitFiling unmounts on success'
  },
  {
    id: WARNING_DICTIONARY_IDS.MTD_SUBMISSION_WARNING_WITH_ERROR, type: TYPES.APPLICATION_WARNING,
    condition: 'Same advisory warning, but on a FAILED GB MTD VAT Return submission. A separate id so it can be rendered inline directly below the error banner without the page-level banner duplicating it'
  },
  {
    id: WARNING_DICTIONARY_IDS.RETURN_APPROVAL_VOIDED, type: TYPES.APPLICATION_WARNING,
    condition: 'If the latest approval of a VAT return was voided by a regenerate, so it must be sent for approval again before it can be marked as submitted',
    behaviour: { timeout: -1 }
  },
  {
    id: WARNING_DICTIONARY_IDS.RETURN_APPROVAL_APPROVED_OUTDATED, type: TYPES.APPLICATION_WARNING,
    condition: 'If the latest approval of a VAT return approved an earlier generation of it, so it must be sent for approval again',
    behaviour: { timeout: -1 }
  },
  {
    id: WARNING_DICTIONARY_IDS.RETURN_APPROVAL_REJECTED_OUTDATED, type: TYPES.APPLICATION_WARNING,
    condition: 'If the latest approval of a VAT return rejected an earlier generation of it, so it must be sent for approval again',
    behaviour: { timeout: -1 }
  },
  {
    id: WARNING_DICTIONARY_IDS.RETURN_APPROVAL_EXPIRED, type: TYPES.APPLICATION_WARNING,
    condition: 'If the approval of a VAT return expired before it was marked as submitted, so it must be sent for approval again',
    behaviour: { timeout: -1 }
  },
  {
    id: WARNING_DICTIONARY_IDS.RETURN_APPROVAL_UNAVAILABLE, type: TYPES.APPLICATION_WARNING,
    condition: 'If the return approval status cannot be checked (503), so sending for approval and marking as submitted are disabled',
    behaviour: { timeout: -1 }
  },
  // End of Warnings

  // Start of Success
  {
    id: SUCCESS_DICTIONARY_IDS.CREATE_NEW_VAT_GROUP_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when new VAT Group is created'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.RESET_PASSWORD_EMAIL_SENT_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when reset password email has been sent'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.NEW_PASSWORD_SET_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when new password has been set'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VAT_RETURN_SUBMIT_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when VAT return submitted '
  },
  {
    id: SUCCESS_DICTIONARY_IDS.MTD_AUTH_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when MTD Authentication Successful '
  },
  {
    id: SUCCESS_DICTIONARY_IDS.CREATE_MANUAL_DOCUMENT_SUCCESS, type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when manual document creation is successful '
  },
  {
    id: SUCCESS_DICTIONARY_IDS.TRANSACTION_ACTION_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if the given action is successful for multiple transaction'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.EXCHANGE_RATES_IMPORT_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Exchange rates import is successful for multiple countries'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VAT_CODES_REDELIVER_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if the given vat codes redeliver is successful for multiple transaction'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.PROCESSED_FILE_REDELIVER_SENT_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if file successfully sent for redelivery'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.PROCESSED_FILE_REDELIVER_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if file successfully redelivered'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.PROCESSED_MULTIPLE_FILES_REDELIVER_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if multiple files successfully redelivered'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.FILE_DELETED_SUCCESSFULLY,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if file is successfully deleted'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VAT_CODE_CREATED_SUCCESSFULLY,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if VAT Code is created successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.FILE_IMPORT_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when XML file is imported in to account settings successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.COMPANIES_IMPORTED_SUCCESSFULLY,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if import companies is successful'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.USER_UPDATE_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if user updated successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VAT_CODE_UPDATED_SUCCESSFULLY,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when a VAT Code is updated successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VAT_CODE_DELETED_SUCCESSFULLY,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when a VAT Code is Deleted successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.INVITE_EMAIL_SENT,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when the Add a user form is successfully submitted and an email is sent'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.SUBMIT_FILING_DE_FILE_UPLOAD_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if certificate file uploaded successfully'
  },
  {
    id: SUCCESS_DICTIONARY_IDS.SUBMIT_FILING_NL_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if filing successfully submitted to digipoort'
  }, {
    id: SUCCESS_DICTIONARY_IDS.SUBMIT_FILING_NL_ALREADY_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if filing already submitted to digipoort',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.VALID_SII_CERTIFICATE,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs if the uploaded SII Certificate is valid',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.COUNTRIES_UNLOCKED_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when countries on hold unlocked is successful',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.COUNTRIES_LOCKED_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when countries on hold locked is successful',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.ALL_COUNTRIES_UNLOCKED_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when all countries on hold unlocked is successful',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.ALL_COUNTRIES_LOCKED_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when all countries on hold locked is successful',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.SAVED_TO_WORKFLOW,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when save to workflow is successful',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.ERRORS_WARNINGS_REOPENED,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when return has been reopened',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.SAVE_TO_WORKFLOW_OK,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success intiatied save to workflow',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.USER_DELETE_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when user is deleted',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.BULK_ACTION_COMPLETED,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'If bulk operation is completed',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.FILE_OPERATION_STARTED_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'If file operation is started',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.COMPANY_PERMISSION_UPDATE_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success occurs when company permissions for user are edited',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.INITIATED_AUTO_SUBMISSION,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success message when auto submission is initiated',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.RETURN_APPROVAL_SENT,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success message when a VAT return is sent for approval',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.IMPORT_EXCHANGE_RATES_SUCCESS,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'Success message when import exchange rates is initiated',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.DOCUMENT_DELETE_OPERATION_STARTED_MESSAGE,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'If a bulk delete operation is started on the Uploaded Documents page',
  },
  {
    id: SUCCESS_DICTIONARY_IDS.DOCUMENT_REDELIVER_OPERATION_STARTED_MESSAGE,
    type: TYPES.APPLICATION_SUCCESS,
    condition: 'If a bulk redeliver operation is started on the Uploaded Documents page',
  },

  /**
   * App Info
   */
  {
    id: INFO_DICTIONARY_IDS.DOCUMENTS_ON_HOLD,
    type: TYPES.APPLICATION_INFO,
    condition: 'If any documents are on hold or in inbox',
  },
  {
    id: INFO_DICTIONARY_IDS.TRANSACTIONS_PENDING,
    type: TYPES.APPLICATION_INFO,
    condition: 'If any documents are in inbox',
  },
  {
    id: INFO_DICTIONARY_IDS.ALL_TRANSACTIONS_PROCESSED,
    type: TYPES.APPLICATION_INFO,
    condition: 'If all on hold documents are processed',
  },
  {
    id: INFO_DICTIONARY_IDS.DOCUMENTS_IN_PROCESSING,
    type: TYPES.APPLICATION_INFO,
    condition: 'If any documents are on hold or in inbox',
  },
  {
    id: INFO_DICTIONARY_IDS.VAT_BOOKS_RETURNS_GENERATION_PENDING,
    type: TYPES.APPLICATION_INFO,
    condition: 'If vat books return generation is already in progress',
  },
  {
    id: INFO_DICTIONARY_IDS.BULK_ACTION_INITIATED,
    type: TYPES.APPLICATION_INFO,
    condition: 'If bulk operation has been initiated',
  },
  {
    id: INFO_DICTIONARY_IDS.BULK_OPERATION_STATUS_NODATA,
    type: TYPES.APPLICATION_INFO,
    condition: 'If get operation status has no data recently',
  },
  {
    id: INFO_DICTIONARY_IDS.INTRASTAT_RETURNS_GENERATION_PENDING,
    type: TYPES.APPLICATION_INFO,
    condition: 'If intrastat return generation is already in progress',
  },
  {
    id: INFO_DICTIONARY_IDS.VAT_RETURN_GENERATION_PENDING,
    type: TYPES.APPLICATION_INFO,
    condition: 'If VAT return generation is already in progress',
  },
  {
    id: INFO_DICTIONARY_IDS.BULK_ACTION_IN_PROGRESS,
    type: TYPES.APPLICATION_INFO,
    condition: 'If bulk operation is in progress',
  },
  {
    id: INFO_DICTIONARY_IDS.IMPORT_EXCHANGE_RATES_STATUS_NODATA,
    type: TYPES.APPLICATION_INFO,
    condition: 'If get import exchanges rates has no data recently',
  },
  {
    id: INFO_DICTIONARY_IDS.IMPORT_EXCHANGE_RATES_STARTED,
    type: TYPES.APPLICATION_INFO,
    condition: 'If import exchange rates has been started',
  },
  {
    id: INFO_DICTIONARY_IDS.UPLOADED_DOCUMENTS_COMPANY_FILTER_PROCESSED_ONLY,
    type: TYPES.APPLICATION_INFO,
    condition: 'Shown on the Uploaded Documents page when file search includes specific company filter',
    behaviour: { timeout: -1 },
  }
  // End of Success
];


const EXTENDED_LIST = BASE_MESSAGE_LIST.concat([

]);

/*
 * To add a new error:
 * 1) Add the ID to the DICTIONARY_IDS above.
 * 2) Add a short-hand entry to the end of the list below.
 * 3) Add language strings. By default these will be inAppMessage_xxx and inAppMessage_xxx_extended, if you haven't specified others to use.
 *
 * Note: skipping 2 or 3, will result in FAILURE to load the app, as these things will be validated at import time.
 *
 * When we fetch the data from the backend, we will be doing a similar validation.
 * Which could be fun, and is the main reason for CANNOT_LOAD_ERRORS, and having a base error list.
 *
 * Note: The full JSON structure per error is expected to be:
 * ```
 * {
 *   "id": 0,
 *   "type": "systemError",
 *   "languageString": "inAppMessage_0",
 *   "extendedLanguageString": "inAppMessage_0_extended",
 *   "behaviour": {
 *     "timeout": -1
 *   },
 *   "link": {
 *     "url": "/help",
 *     "languageString": "inAppMessage_default_FindOutMoreLinkText"
 *   },
 *   "technical": {
 *     "condition": "Error occurs if an in-app error ID is used which is not recognised",
 *     "usage": "Placeholder for future use"
 *   }
 * }
 * ```
 * However, that's a bit of a hassle to create manually every time, since many of the fields can be inferred by convention,
 * and others simply have default values for now.
 *
 * So the there is a short-hand form (used in the lists above), and the helpers / classes below will take either the long-hand or short-hand forms
 * and convert to the standardised long-hand form.
 *
 */

function checkLanguageString(languageString, name) {
  if (!languageStrings[languageString]) {
    throw new Error(format('%s (%s) is not present in the languageStrings data', name, languageString));
  }
}
class LinkDetails {
  constructor(options) {
    // If no params passed to the constructor, just return without trying to initialise anything.
    if (!options || typeof options !== 'object') {
      return this;
    }

    let { url, linkLanguageString } = options;
    this.url = url || '/help';
    this.languageString = linkLanguageString || 'inAppMessage_default_FindOutMoreLinkText';
    checkLanguageString(this.languageString, 'linkLanguageString');
  }
}

class TechnicalDetails {
  constructor(options) {
    // If no params passed to the constructor, just return without trying to initialise anything.
    if (!options || typeof options !== 'object') {
      return this;
    }

    let { condition, usage } = options;
    this.condition = condition || 'None specified';
    this.usage = usage || 'Placeholder for future use';
  }
}

class Behaviour {
  constructor(options) {
    // If no params passed to the constructor, just return without trying to initialise anything.
    if (!options || typeof options !== 'object') {
      return this;
    }
    let { timeout } = options;
    timeout = parseInt(timeout);
    this.timeout = isNaN(timeout) ? -1 : timeout;
  }
}

class inAppMessage {
  constructor(options) {
    // If no params passed to the constructor, just return without trying to initialise anything.
    if (!options || typeof options !== 'object') {
      return this;
    }

    let { id, type, languageString, extendedLanguageString, behaviour, link, technical } = options;
    this.id = id;
    // if (isNaN(this.id)) {
    //   throw new Error(format('id (%s) is not a number', id));
    // }

    this.type = type;
    if (Object.values(TYPES).indexOf(type) < 0) {
      throw new Error(format('type "%s" must be one of %j', type, Object.values(TYPES)));
    }

    if (Object.keys(ALL_DICTIONARY_IDS).length !== (Object.keys(SYS_ERROR_DICTIONARY_IDS).length + Object.keys(NETWORK_ERROR_DICTIONARY_IDS).length + Object.keys(APP_ERROR_DICTIONARY_IDS).length + Object.keys(WARNING_DICTIONARY_IDS).length + Object.keys(SUCCESS_DICTIONARY_IDS).length + Object.keys(INFO_DICTIONARY_IDS).length)) {
      throw new Error(format('Collision test failed - ALL_DICTIONARY_IDS number of values to not match the number of values of its child dictionaries'));
    }

    languageString = typeof languageString === 'string' ? languageString : format('inAppMessage_%s', this.id);
    if (languageString === '') {
      throw new Error(format('languageString ("%s"[%s]) must be a non-blank string', languageString, typeof languageString));
    }
    this.languageString = languageString;
    checkLanguageString(this.languageString, 'languageString');

    extendedLanguageString = typeof extendedLanguageString === 'string' ? extendedLanguageString : format('inAppMessage_%s_extended', this.id);
    if (extendedLanguageString === '') {
      throw new Error(format('extendedLanguageString ("%s"[%s]) must be a non-blank string', extendedLanguageString, typeof extendedLanguageString));
    }
    this.extendedLanguageString = extendedLanguageString;
    checkLanguageString(this.extendedLanguageString, 'extendedLanguageString');

    this.behaviour = behaviour instanceof Behaviour ? behaviour : typeof behaviour === 'object' ? new Behaviour(behaviour) : new Behaviour(options);
    this.link = link instanceof LinkDetails ? link : typeof link === 'object' ? new LinkDetails(link) : new LinkDetails(options);
    this.technical = technical instanceof TechnicalDetails ? technical : typeof technical === 'object' ? new TechnicalDetails(technical) : new TechnicalDetails(options);
  }

}

function toDictionary(array, keyField) {
  keyField = keyField || 'id';

  if (!Array.isArray(array)) {
    return {}
  }
  return array.reduce((dictionary, entry) => {
    dictionary[entry[keyField]] = entry;
    return dictionary;
  }, {});
}

function hasUniqueKeys(array, keyField) {
  keyField = keyField || 'id';
  if (!Array.isArray(array)) {
    return false;
  }

  const keys = array.map((entry) => { return entry[keyField] });
  const unique = keys.filter((v, i, a) => { return a.indexOf(v) === i });
  return keys.length === unique.length;
}

// Reason for the optional cb is that without it, we fail at the first error, with it we gather the errors.
// This is useful for overloading the function, so that it can be used to fast fail (on app load) if any of the BASE_MESSAGE_LIST is wrong,
// but can gather all the errors for the extended list which will, one day, hopefully be served by the backend.
function asStructuredAndCheckedDictionary(list, options, cb) {
  options = options || {};
  const gatherErrors = typeof cb === 'function';
  const enforceIdCheck = options.enforceIdCheck;
  const errors = [];
  if (!Array.isArray(list)) {
    errors.push(new Error(format('list is not an array: %s', list)));
    if (gatherErrors) return cb(errors);
    throw errors[0];
  }

  if (!hasUniqueKeys(list, 'id')) {
    errors.push(new Error(format('list does not have unique ids: %j', list)));
    if (gatherErrors) return cb(errors);
    throw errors[0];
  }

  const dictionary = toDictionary(list.map((entry) => {
    try {
      return new inAppMessage(entry)
    }
    catch (e) {
      if (gatherErrors) {
        errors.push(e);
      }
      else {
        throw e;
      }
    }
  }).filter((entry) => { return entry !== undefined; }));

  if (enforceIdCheck) {
    for (var key in APP_ERROR_DICTIONARY_IDS) {
      if (!dictionary[APP_ERROR_DICTIONARY_IDS[key]]) {
        errors.push(new Error(format('list does not have contain an entry for %s (%s)', key, APP_ERROR_DICTIONARY_IDS[key])));
        if (!gatherErrors) {
          throw errors[errors.length - 1];
        }
      }
    }
  }

  if (gatherErrors) {
    cb(errors.length > 0 ? errors : undefined, dictionary)
  }

  return dictionary;
}


const EXTENDED_DICTIONARY = asStructuredAndCheckedDictionary(EXTENDED_LIST, { enforceIdCheck: true });

export const BASE_MESSAGE_DICTIONARY = asStructuredAndCheckedDictionary(BASE_MESSAGE_LIST);

export default {
  loadErrorDictionary() {
    return new Promise((resolve, reject) => {
      // TODO: Fetch from backend
      // asStructuredAndCheckedDictionary(dataFromBackend, {enforceIdCheck: true}, (errors, dictionary) => {
      //   if (errors) {
      //     // errors.forEach((error) => {
      //     //   console.error('NOT SAAS_VR_WEB_APP]: error: ', e rror);
      //     // });
      //     reject({error: errors, dictionary: dictionary});
      //   }
      //   resolve(dictionary);
      // });

      setTimeout(() => {
        resolve(EXTENDED_DICTIONARY);
      }, 2000)
    });
  }

}
